import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Power, AlertTriangle, ChevronUp, ChevronDown, Clock, Luggage, CloudLightning,
  FileText, Search, X, Loader2, Globe, Plane, RotateCcw, FlaskConical,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { templatesAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';
import EmptyState from '../../components/UI/EmptyState';
import { formatOffset, offsetToSeconds } from '../../utils/offsetLabel';

// ── Template picker (reuses the shared Templates library, /admin/templates) ────
// Template placeholders are PascalCase (from the generic multi-industry Template model);
// Announcement Rules placeholders are snake_case and a fixed small set per rule type. Best-
// effort remap the ones with a clear equivalent; anything else is left as literal text in
// the inserted template and flagged so the admin knows to edit or remove it.
const TEMPLATE_PLACEHOLDER_MAP = {
  FlightNumber: 'flight_number',
  Destination: 'destination',
  Origin: 'origin',
  AirlineCode: 'airline',
  Gate: 'gate',
  CarouselNumber: 'belt',
};

function remapTemplateText(templateText) {
  const placeholders = [...new Set([...templateText.matchAll(/\{([^}]+)\}/g)].map(m => m[1]))];
  let text = templateText;
  const unmapped = [];
  for (const ph of placeholders) {
    const mapped = TEMPLATE_PLACEHOLDER_MAP[ph];
    if (mapped) {
      text = text.split(`{${ph}}`).join(`{${mapped}}`);
    } else {
      unmapped.push(ph);
    }
  }
  return { text, unmapped };
}

function TemplatePickerModal({ onClose, onSelect }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Airport');
  const [categories, setCategories] = useState(['Airport']);

  useEffect(() => {
    templatesAPI.getCategories()
      .then(res => setCategories(res.data.data?.categories || ['Airport']))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    templatesAPI.getByCategory(category)
      .then(res => {
        const rows = res.data.data?.templates || res.data.data || [];
        setTemplates(Array.isArray(rows) ? rows : []);
      })
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  }, [category]);

  const filtered = templates.filter(t =>
    !search ||
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.template_text.toLowerCase().includes(search.toLowerCase())
  );

  function handlePick(t) {
    const { text, unmapped } = remapTemplateText(t.template_text);
    onSelect(text);
    if (unmapped.length > 0) {
      toast(`Inserted — but {${unmapped.join('}, {')}} aren't supported here and will show literally unless you edit them.`, { icon: '⚠️', duration: 6000 });
    } else {
      toast.success('Template inserted');
    }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">Use a Template</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0 space-y-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                  category === c ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-3 py-2">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 size={22} className="animate-spin text-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">No templates match.</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {filtered.map(t => (
                <button
                  key={t.id}
                  onClick={() => handlePick(t)}
                  className="w-full text-left px-2 py-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <p className="text-sm font-semibold text-gray-800">{t.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{t.template_text}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Event-triggered types (belt/weather) — configured on the "Baggage & Weather" tab
// instead of the departure-offset table, since sequence_order doesn't apply to them.
// offset_minutes DOES apply to belt_change/first_bag — "minutes after touchdown /
// first-bag-presented" (the arrival-side mirror of gate rules' "minutes before
// departure"). weather_alert has no per-flight reference time to offset from — it's
// raised manually, not tied to a single flight's timeline — so it has no offset field.
const BAGGAGE_TYPE_VALUES = ['belt_change', 'first_bag', 'weather_alert'];

const BAGGAGE_TYPE_META = {
  belt_change:   { label: 'Belt Change',    icon: Luggage,        tone: 'purple', placeholders: ['flight_number', 'airline', 'belt'], offsetLabel: 'Minutes after touchdown' },
  first_bag:     { label: 'First Bag',      icon: Luggage,        tone: 'teal',   placeholders: ['flight_number', 'terminal', 'belt'], offsetLabel: 'Minutes after first bag presented' },
  weather_alert: { label: 'Weather Alert',  icon: CloudLightning, tone: 'danger', placeholders: [], offsetLabel: null },
};

const ANNOUNCEMENT_TYPES = [
  { value: 'pre_boarding',       label: 'Pre-Boarding' },
  { value: 'boarding_call',      label: 'Boarding Call' },
  { value: 'boarding_reminder',  label: 'Boarding Reminder' },
  { value: 'final_boarding',     label: 'Final Boarding' },
  { value: 'gate_closing',       label: 'Gate Closing' },
  { value: 'gate_closed',        label: 'Gate Closed' },
  { value: 'custom',             label: 'Custom' },
];

const TYPE_TONES = {
  pre_boarding:      'info',
  boarding_call:     'success',
  boarding_reminder: 'yellow',
  final_boarding:    'orange',
  gate_closing:      'danger',
  gate_closed:       'neutral',
  custom:            'purple',
};

const EMPTY_FORM = {
  name: '',
  announcement_type: 'boarding_call',
  type_label: '', // only used (and required) when announcement_type is 'custom'
  // Offset is edited as minutes + seconds and sent as offset_seconds
  offset_min: 45,
  offset_sec: 0,
  default_english_text: '',
  repeat_count: 1,
  sequence_order: 10,
};

export default function AdminRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // rule id to confirm
  const [activeTab, setActiveTab] = useState('gate'); // 'gate' | 'baggage'
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  // Per-airline rule sets: null = Global; an IATA code = that airline's own set
  const [ruleSets, setRuleSets] = useState([]);
  const [knownAirlines, setKnownAirlines] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [showAddAirline, setShowAddAirline] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);

  useEffect(() => { loadRules(); loadRuleSets(); }, []);

  async function loadRuleSets() {
    try {
      const res = await api.get('/admin/rule-sets');
      setRuleSets(res.data.data?.sets || []);
      setKnownAirlines(res.data.data?.airlines || []);
    } catch { /* non-critical: Global still works */ }
  }

  const currentSet = ruleSets.find(rs => rs.airline_code === selectedSet) || null;

  async function toggleSetActive() {
    try {
      await api.patch(`/admin/rule-sets/${selectedSet}`, { is_active: !currentSet.is_active });
      toast.success(currentSet.is_active ? `${selectedSet} rules turned off — its flights use Global` : `${selectedSet} rules turned on`);
      loadRuleSets();
    } catch { toast.error('Failed to update rule set'); }
  }

  async function resetSetToGlobal() {
    try {
      await api.delete(`/admin/rule-sets/${selectedSet}`);
      toast.success(`${selectedSet} reset — its flights now use Global rules`);
      setResetConfirm(false);
      setSelectedSet(null);
      loadRules(); loadRuleSets();
    } catch { toast.error('Failed to reset rule set'); }
  }

  async function loadRules() {
    try {
      const res = await api.get('/admin/rules');
      setRules(res.data.data || []);
    } catch {
      toast.error('Failed to load announcement rules');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  }

  function openEdit(rule) {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      announcement_type: rule.announcement_type,
      type_label: rule.type_label || '',
      offset_min: Math.floor(offsetToSeconds(rule.offset_minutes) / 60),
      offset_sec: offsetToSeconds(rule.offset_minutes) % 60,
      default_english_text: rule.default_english_text || '',
      repeat_count: rule.repeat_count,
      sequence_order: rule.sequence_order,
    });
    setModalOpen(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.announcement_type === 'custom' && !form.type_label.trim()) { toast.error('Enter a name for the custom announcement type'); return; }
    const { offset_min, offset_sec, ...rest } = form;
    const payload = { ...rest, offset_seconds: (parseInt(offset_min) || 0) * 60 + (parseInt(offset_sec) || 0) };
    try {
      setSaving(true);
      if (editingRule) {
        await api.put(`/admin/rules/${editingRule.id}`, payload);
        toast.success('Rule updated');
      } else {
        await api.post('/admin/rules', { ...payload, airline_code: selectedSet });
        toast.success('Rule created');
      }
      setModalOpen(false);
      loadRules();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(rule) {
    try {
      await api.patch(`/admin/rules/${rule.id}/toggle`);
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
      toast.success(rule.is_active ? 'Rule deactivated' : 'Rule activated');
    } catch {
      toast.error('Failed to toggle rule');
    }
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/admin/rules/${id}`);
      setRules(prev => prev.filter(r => r.id !== id));
      setDeleteConfirm(null);
      toast.success('Rule deleted');
    } catch {
      toast.error('Failed to delete rule');
    }
  }

  async function moveRule(rule, direction) {
    // Within the selected set's gate rules only (not baggage, not other airlines)
    const sorted = sortedRules;
    const idx = sorted.findIndex(r => r.id === rule.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const other = sorted[swapIdx];
    const thisOrder = rule.sequence_order;
    const otherOrder = other.sequence_order;

    try {
      await Promise.all([
        api.put(`/admin/rules/${rule.id}`, { sequence_order: otherOrder }),
        api.put(`/admin/rules/${other.id}`, { sequence_order: thisOrder }),
      ]);
      setRules(prev => prev.map(r => {
        if (r.id === rule.id) return { ...r, sequence_order: otherOrder };
        if (r.id === other.id) return { ...r, sequence_order: thisOrder };
        return r;
      }));
    } catch {
      toast.error('Failed to reorder');
    }
  }

  const sortedRules = [...rules]
    .filter(r => !BAGGAGE_TYPE_VALUES.includes(r.announcement_type))
    .filter(r => (r.airline_code || null) === selectedSet)
    .sort((a, b) => a.sequence_order - b.sequence_order);
  // Baggage & weather rules are Global only
  const baggageRules = rules.filter(r => BAGGAGE_TYPE_VALUES.includes(r.announcement_type) && !r.airline_code);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Announcement Rules"
        subtitle={activeTab === 'gate'
          ? 'Configure the scheduled announcement sequence. Rules run in sequence order at T-minus the offset.'
          : 'Configure the wording for belt-change, first-bag, and weather-alert announcements.'}
        actions={activeTab === 'gate' && (
          <Button variant="primary" icon={Plus} onClick={openCreate}>New Rule</Button>
        )}
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {[['gate', 'Gate Announcements'], ['baggage', 'Baggage & Weather']].map(([value, label]) => (
          <button
            key={value}
            onClick={() => setActiveTab(value)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'baggage' ? (
        <BaggageRulesSection rules={baggageRules} onSaved={loadRules} />
      ) : (
      <>
      {/* Airline rule sets: Global + one per airline */}
      <AirlineSetSwitcher sets={ruleSets} selected={selectedSet} onSelect={setSelectedSet} onAdd={() => setShowAddAirline(true)} />

      {/* Which flights use the selected set */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4 text-sm text-blue-800 flex gap-2">
        {selectedSet ? <Plane size={16} className="flex-shrink-0 mt-0.5" /> : <Globe size={16} className="flex-shrink-0 mt-0.5" />}
        <span>
          {selectedSet ? (
            currentSet?.is_active
              ? <>Flights operated by <strong>{currentSet.airline_name || selectedSet} ({selectedSet})</strong> use <strong>only</strong> these rules. All other airlines use Global.</>
              : <><strong>{currentSet?.airline_name || selectedSet} ({selectedSet})</strong> rules are <strong>turned off</strong> — its flights currently use Global.</>
          ) : (
            <>Used for every airline{ruleSets.filter(rs => rs.is_active && rs.active_rule_count > 0).length > 0
              ? <> except <strong>{ruleSets.filter(rs => rs.is_active && rs.active_rule_count > 0).map(rs => rs.airline_code).join(', ')}</strong>, which have their own rules</>
              : ''}.</>
          )}{' '}
          Rules apply when a flight's programme is initiated; sessions already in progress are <strong>not</strong> affected.
        </span>
      </div>

      {selectedSet && currentSet && (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
            <input type="checkbox" checked={currentSet.is_active} onChange={toggleSetActive} className="w-4 h-4 accent-blue-600" />
            Use {selectedSet} rules for {selectedSet} flights
          </label>
          <button onClick={() => setResetConfirm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-red-600 border border-red-200 bg-white hover:bg-red-50">
            <RotateCcw size={13} /> Reset to Global
          </button>
        </div>
      )}

      <AnnouncementGapSetting />

      <TestFlightPanel />

      {/* Rules table */}
      {sortedRules.length === 0 ? (
        <EmptyState icon={Clock} title="No rules yet" subtitle="Create your first announcement rule to get started" />
      ) : (
        <Card padded={false} className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 font-semibold text-gray-600 w-10">#</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Rule Name</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Offset</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Repeat</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Active</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRules.map((rule, idx) => (
                <tr key={rule.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${!rule.is_active ? 'opacity-50' : ''}`}>
                  {/* Sequence order + reorder buttons */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col items-center gap-0.5">
                      <button
                        onClick={() => moveRule(rule, 'up')}
                        disabled={idx === 0}
                        className="disabled:opacity-20 text-gray-400 hover:text-gray-700"
                      >
                        <ChevronUp size={13} />
                      </button>
                      <span className="text-xs font-mono text-gray-500">{rule.sequence_order}</span>
                      <button
                        onClick={() => moveRule(rule, 'down')}
                        disabled={idx === sortedRules.length - 1}
                        className="disabled:opacity-20 text-gray-400 hover:text-gray-700"
                      >
                        <ChevronDown size={13} />
                      </button>
                    </div>
                  </td>

                  {/* Name + preview text */}
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{rule.name}</div>
                    {rule.default_english_text && (
                      <div className="text-xs text-gray-400 mt-0.5 line-clamp-1 max-w-xs">
                        {rule.default_english_text}
                      </div>
                    )}
                  </td>

                  {/* Type badge */}
                  <td className="px-4 py-3">
                    <StatusBadge
                      label={rule.type_label || ANNOUNCEMENT_TYPES.find(t => t.value === rule.announcement_type)?.label || rule.announcement_type}
                      tone={TYPE_TONES[rule.announcement_type] || 'neutral'}
                    />
                  </td>

                  {/* Offset */}
                  <td className="px-4 py-3 font-mono text-gray-700">
                    T+{formatOffset(rule.offset_minutes)}
                  </td>

                  {/* Repeat */}
                  <td className="px-4 py-3 text-center text-gray-700">
                    ×{rule.repeat_count}
                  </td>

                  {/* Active toggle */}
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleToggle(rule)}
                      title={rule.is_active ? 'Deactivate' : 'Activate'}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                        rule.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                    >
                      <Power size={14} />
                    </button>
                  </td>

                  {/* Edit / Delete */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(rule)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(rule.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Stats footer */}
      <p className="text-xs text-gray-400 mt-3 text-right">
        {sortedRules.filter(r => r.is_active).length} active / {sortedRules.length} total rules
      </p>

      {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingRule ? 'Edit Rule' : 'New Announcement Rule'}
              </h2>
            </div>

            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Pre-Boarding Notice"
                  required
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Type + Offset side-by-side */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Announcement Type *</label>
                  <select
                    value={form.announcement_type}
                    onChange={e => setForm(f => ({ ...f, announcement_type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {ANNOUNCEMENT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  {/* Custom: the admin names the type — shown in the queue instead of "Custom" */}
                  {form.announcement_type === 'custom' && (
                    <div className="mt-2">
                      <input
                        type="text"
                        aria-label="Custom announcement type"
                        value={form.type_label}
                        onChange={e => setForm(f => ({ ...f, type_label: e.target.value }))}
                        placeholder="Type name, e.g. Lounge Closing"
                        maxLength={60}
                        required
                        autoFocus
                        className="w-full border border-purple-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <span className="block text-[11px] font-medium text-gray-500 mt-0.5">Shown as the type in the gate queue</span>
                    </div>
                  )}
                </div>
                <div className="w-44">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Offset after Initiate *</label>
                  <div className="flex gap-1.5">
                    {/* Explicit Minutes / Seconds labels — a value typed into the
                        wrong box (10 min instead of 10 s) is easy to miss otherwise */}
                    <div className="flex-1">
                      <input
                        type="number"
                        aria-label="Offset minutes"
                        value={form.offset_min}
                        onChange={e => setForm(f => ({ ...f, offset_min: e.target.value.replace(/\D/g, '') }))}
                        min={0}
                        max={1440}
                        step={1}
                        required
                        className="w-full border border-gray-300 rounded-xl px-2.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="block text-[11px] font-medium text-gray-500 mt-0.5 text-center">Minutes</span>
                    </div>
                    <div className="flex-1">
                      <input
                        type="number"
                        aria-label="Offset seconds"
                        value={form.offset_sec}
                        onChange={e => setForm(f => ({ ...f, offset_sec: e.target.value.replace(/\D/g, '') }))}
                        min={0}
                        max={59}
                        step={1}
                        className="w-full border border-gray-300 rounded-xl px-2.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="block text-[11px] font-medium text-gray-500 mt-0.5 text-center">Seconds</span>
                    </div>
                  </div>
                  <p className="text-xs font-semibold text-blue-700 mt-1">
                    Plays {formatOffset(((parseInt(form.offset_min) || 0) * 60 + (parseInt(form.offset_sec) || 0)) / 60)} after Initiate
                  </p>
                </div>
              </div>

              {/* Repeat + Sequence side-by-side */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Repeat Count</label>
                  <input
                    type="number"
                    value={form.repeat_count}
                    onChange={e => setForm(f => ({ ...f, repeat_count: e.target.value }))}
                    min={1}
                    max={5}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">How many times to play</p>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sequence Order</label>
                  <input
                    type="number"
                    value={form.sequence_order}
                    onChange={e => setForm(f => ({ ...f, sequence_order: e.target.value }))}
                    min={1}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">Lower = earlier</p>
                </div>
              </div>

              {/* Default English text */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Default English Text</label>
                  <button
                    type="button"
                    onClick={() => setShowTemplatePicker(true)}
                    className="flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
                  >
                    <FileText size={12} /> Use Template
                  </button>
                </div>
                <textarea
                  value={form.default_english_text}
                  onChange={e => setForm(f => ({ ...f, default_english_text: e.target.value }))}
                  rows={4}
                  placeholder="Use {flight_number}, {destination}, {gate}, {airline} as placeholders"
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Placeholders: <code className="bg-gray-100 px-1 rounded">{'{flight_number}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{destination}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{gate}'}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{'{airline}'}</code>
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="ghost" onClick={() => setModalOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={saving} className="flex-1">
                  {editingRule ? 'Save Changes' : 'Create Rule'}
                </Button>
              </div>
            </form>
          </div>

          {showTemplatePicker && (
            <TemplatePickerModal
              onClose={() => setShowTemplatePicker(false)}
              onSelect={(text) => setForm(f => ({ ...f, default_english_text: text }))}
            />
          )}
        </div>
      )}

      {showAddAirline && (
        <AddAirlineModal
          airlines={knownAirlines.filter(a => !ruleSets.some(rs => rs.airline_code === a.code))}
          onClose={() => setShowAddAirline(false)}
          onCreated={(code) => { setShowAddAirline(false); setSelectedSet(code); loadRules(); loadRuleSets(); }}
        />
      )}

      {resetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-2">Reset {selectedSet} to Global?</h3>
            <p className="text-sm text-gray-600 mb-5">
              This deletes {selectedSet}'s {sortedRules.length} rule{sortedRules.length === 1 ? '' : 's'}. {selectedSet} flights initiated from now on will use the Global rules. Sessions already in progress keep their programme.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setResetConfirm(false)}>Cancel</Button>
              <Button variant="danger" onClick={resetSetToGlobal}>Reset to Global</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ──────────────────────────────────────── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Delete Rule?</h3>
                <p className="text-sm text-gray-500">This cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="flex-1">
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleDelete(deleteConfirm)} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ── Baggage & Weather tab ────────────────────────────────────────────────────
// Event-triggered (belt-change / first-bag / weather-alert), not scheduled by
// offset — a fixed set of 3 rules seeded by migration 017, editable here but not
// creatable/deletable (flightSyncService.js / weatherAlertService.js look them up
// by announcement_type, so an admin can't rename that away).

// ── Airline rule sets ──────────────────────────────────────────────────────────

function AirlineSetSwitcher({ sets, selected, onSelect, onAdd }) {
  const chip = (active) => `flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold border transition-colors ${
    active ? 'bg-blue-700 text-white border-blue-700 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'}`;
  return (
    <div className="flex items-center gap-2 flex-wrap mb-4">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 mr-1">Rules for</span>
      <button onClick={() => onSelect(null)} className={chip(selected === null)}>
        <Globe size={14} /> Global <span className="font-normal opacity-75">(default)</span>
      </button>
      {sets.map(rs => (
        <button key={rs.airline_code} onClick={() => onSelect(rs.airline_code)} className={chip(selected === rs.airline_code)}
          title={rs.airline_name || rs.airline_code}>
          <Plane size={13} /> {rs.airline_code}
          {rs.airline_name && <span className="font-normal opacity-75 max-w-[140px] truncate">· {rs.airline_name}</span>}
          {!rs.is_active && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">OFF</span>}
        </button>
      ))}
      <button onClick={onAdd} className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-semibold text-blue-700 border border-dashed border-blue-300 hover:bg-blue-50">
        <Plus size={14} /> Add airline
      </button>
    </div>
  );
}

function AddAirlineModal({ airlines, onClose, onCreated }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [copyGlobal, setCopyGlobal] = useState(true);
  const [saving, setSaving] = useState(false);

  function pick(c) {
    setCode(c);
    const a = airlines.find(x => x.code === c);
    if (a && a.name && a.name !== c) setName(a.name);
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post('/admin/rule-sets', { airline_code: code, airline_name: name, copy_global: copyGlobal });
      toast.success(copyGlobal
        ? `${code} added with ${res.data.data.copied} rules copied from Global — edit them as needed`
        : `${code} added — now create its rules`);
      onCreated(code);
    } catch (err) {
      if (!err._toasted) toast.error(err.response?.data?.message || 'Failed to add airline');
    } finally { setSaving(false); }
  }

  const options = [
    [true, 'Copy the Global rules', 'Fastest — then adjust what differs for this airline'],
    [false, 'Start empty', "Build this airline's rules from scratch"],
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Add airline rules</h3>
          <p className="text-sm text-gray-500 mt-0.5">This airline's flights will use its own rules instead of Global.</p>
        </div>
        {airlines.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {airlines.map(a => (
              <button type="button" key={a.code} onClick={() => pick(a.code)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${code === a.code ? 'bg-blue-700 text-white border-blue-700' : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-blue-300'}`}>
                {a.code}{a.name && a.name !== a.code ? ` · ${a.name}` : ''}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          <label className="w-28">
            <span className="block text-sm font-medium text-gray-700 mb-1">IATA code *</span>
            <input value={code} onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3))}
              placeholder="TR" required className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </label>
          <label className="flex-1">
            <span className="block text-sm font-medium text-gray-700 mb-1">Airline name</span>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Scoot"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </label>
        </div>
        <div className="space-y-2">
          <span className="block text-sm font-medium text-gray-700">Start from</span>
          {options.map(([v, title, hint]) => (
            <label key={String(v)} className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer ${copyGlobal === v ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
              <input type="radio" checked={copyGlobal === v} onChange={() => setCopyGlobal(v)} className="mt-0.5 accent-blue-600" />
              <span>
                <span className="block text-sm font-semibold text-gray-800">{title}</span>
                <span className="block text-xs text-gray-500">{hint}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={saving} disabled={code.length < 2}>Add airline</Button>
        </div>
      </form>
    </div>
  );
}

// Silence between consecutive announcements at a gate — applies to every airline's
// rules and to airport announcements sent to a gate; emergency skips it; the
// first announcement after Initiate always plays straight away.
function AnnouncementGapSetting() {
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/flights/sync/config').then(res => {
      const v = res.data.data?.announcementGapSeconds ?? 10;
      setValue(String(v)); setSaved(v);
    }).catch(() => {});
  }, []);

  async function save(e) {
    e.preventDefault();
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0 || n > 120) { toast.error('Enter whole seconds from 0 to 120'); return; }
    setSaving(true);
    try {
      await api.put('/flights/sync/config', { announcementGapSeconds: n });
      setSaved(n);
      toast.success(n === 0 ? 'Pause turned off — announcements play back to back' : `Pause set to ${n} seconds`);
    } catch (err) { if (!err._toasted) toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  return (
    <form onSubmit={save} className="mb-4 rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center gap-2 flex-wrap">
      <Clock size={15} className="text-gray-400" />
      <span className="text-sm font-medium text-gray-700">Pause between announcements</span>
      <input type="number" min={0} max={120} step={1} value={value} onChange={e => setValue(e.target.value.replace(/\D/g, ''))}
        className="w-20 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" />
      <span className="text-sm text-gray-500">seconds</span>
      <Button type="submit" variant="primaryLight" size="sm" loading={saving} disabled={value === '' || Number(value) === saved}>Save</Button>
      <span className="text-xs text-gray-400 basis-full sm:basis-auto sm:ml-1">
        After each announcement ends, the next one waits this long. First announcement after Initiate plays immediately; emergency skips the pause.
      </span>
    </form>
  );
}

// Type a flight number → which rule set it gets and the programme that would run
function TestFlightPanel() {
  const [fn, setFn] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function test(e) {
    e.preventDefault();
    if (!fn.trim()) return;
    setLoading(true);
    try {
      const res = await api.get('/admin/rule-sets/resolve', { params: { flight_number: fn.trim() } });
      setResult(res.data.data);
    } catch {
      setResult(null);
      toast.error('Could not check that flight');
    } finally { setLoading(false); }
  }

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white px-4 py-3">
      <form onSubmit={test} className="flex items-center gap-2 flex-wrap">
        <FlaskConical size={15} className="text-gray-400" />
        <span className="text-sm font-medium text-gray-700">Test a flight</span>
        <input value={fn} onChange={e => setFn(e.target.value.toUpperCase())} placeholder="e.g. TR438"
          className="w-32 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <Button type="submit" variant="primaryLight" size="sm" loading={loading}>Check</Button>
        {result && (
          <span className="text-sm text-gray-700">
            <strong>{result.flight_number}</strong> ({result.airline || result.airline_code}) uses{' '}
            <strong className={result.rule_set.airline_code ? 'text-blue-700' : 'text-gray-900'}>
              {result.rule_set.airline_code ? `${result.rule_set.airline_name} rules` : 'Global rules'}
            </strong>
          </span>
        )}
      </form>
      {result && result.rules.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {result.rules.map(r => (
            <span key={r.id} className="text-xs px-2 py-1 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
              {r.name} <span className="font-mono text-gray-500">T+{formatOffset(r.offset_minutes)}</span>{r.repeat_count > 1 ? ` ×${r.repeat_count}` : ''}
            </span>
          ))}
        </div>
      )}
      {result && result.rules.length === 0 && (
        <p className="text-xs text-red-600 mt-2">No active rules — this flight would have no announcement programme.</p>
      )}
    </div>
  );
}

const EMPTY_BAGGAGE_FORM = { name: '', announcement_type: 'belt_change', default_english_text: '', repeat_count: 2, offset_minutes: 0 };

function BaggageRulesSection({ rules, onSaved }) {
  const [editingRule, setEditingRule] = useState(null);
  const [text, setText] = useState('');
  const [repeatCount, setRepeatCount] = useState(2);
  const [offsetMinutes, setOffsetMinutes] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_BAGGAGE_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [templatePickerFor, setTemplatePickerFor] = useState(null); // 'edit' | 'create' | null

  function openEdit(rule) {
    setEditingRule(rule);
    setText(rule.default_english_text || '');
    setRepeatCount(rule.repeat_count);
    setOffsetMinutes(rule.offset_minutes ?? 0);
  }

  function openCreate() {
    setCreateForm(EMPTY_BAGGAGE_FORM);
    setShowCreateModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/rules/${editingRule.id}`, {
        default_english_text: text,
        repeat_count: parseInt(repeatCount) || 1,
        ...(BAGGAGE_TYPE_META[editingRule.announcement_type]?.offsetLabel
          ? { offset_minutes: parseInt(offsetMinutes) || 0 }
          : {}),
      });
      toast.success('Rule updated');
      setEditingRule(null);
      onSaved();
    } catch {
      toast.error('Failed to save rule');
    } finally {
      setSaving(false);
    }
  }

  // Only one active rule per type may exist — flightSyncService.js/weatherAlertService.js
  // look up the active rule with findOne(), so a second active row of the same type is
  // ambiguous and silently unpredictable (this is exactly the bug that made every gate
  // announcement fire 4x). Creating a new active rule auto-deactivates any existing one
  // of the same type instead of letting that state occur.
  async function handleCreate(e) {
    e.preventDefault();
    if (!createForm.name.trim()) { toast.error('Name is required'); return; }
    if (!createForm.default_english_text.trim()) { toast.error('Script is required'); return; }

    setSaving(true);
    try {
      const conflicting = rules.find(r => r.announcement_type === createForm.announcement_type && r.is_active);
      if (conflicting) {
        await api.patch(`/admin/rules/${conflicting.id}/toggle`);
      }
      await api.post('/admin/rules', {
        name: createForm.name,
        announcement_type: createForm.announcement_type,
        offset_minutes: BAGGAGE_TYPE_META[createForm.announcement_type]?.offsetLabel
          ? (parseInt(createForm.offset_minutes) || 0)
          : 0,
        sequence_order: 0,
        default_english_text: createForm.default_english_text,
        repeat_count: parseInt(createForm.repeat_count) || 1,
      });
      toast.success(
        conflicting
          ? `Rule created — deactivated the previous "${BAGGAGE_TYPE_META[createForm.announcement_type]?.label}" rule to avoid duplicates`
          : 'Rule created'
      );
      setShowCreateModal(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create rule');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/admin/rules/${id}`);
      toast.success('Rule deleted — built-in wording will be used until a new one is added');
      setDeleteConfirm(null);
      onSaved();
    } catch {
      toast.error('Failed to delete rule');
    }
  }

  async function handleToggle(rule) {
    try {
      await api.patch(`/admin/rules/${rule.id}/toggle`);
      toast.success(rule.is_active ? 'Rule deactivated — falling back to built-in wording' : 'Rule activated');
      onSaved();
    } catch {
      toast.error('Failed to toggle rule');
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex gap-2 flex-1">
          <CloudLightning size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            These fire automatically from FlightMS/weather events. Belt Change and First Bag support an
            offset (e.g. "5 minutes after touchdown") so the PA doesn't go out before passengers have
            actually landed; Weather Alert always fires immediately since it isn't tied to one flight's
            timeline. If a rule is deactivated or its text is left blank, the system falls back to its
            built-in wording — the announcement still fires, just with default phrasing.
          </span>
        </div>
        <Button variant="primary" icon={Plus} onClick={openCreate} className="flex-shrink-0">
          New Rule
        </Button>
      </div>

      <Card padded={false} className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Rule</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Script</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600">Offset</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600">Repeat</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600">Active</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.map(rule => {
              const meta = BAGGAGE_TYPE_META[rule.announcement_type] || {};
              return (
                <tr key={rule.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${!rule.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <StatusBadge label={meta.label || rule.name} tone={meta.tone || 'neutral'} />
                  </td>
                  <td className="px-4 py-3 text-gray-600 max-w-md">
                    <div className="line-clamp-2">{rule.default_english_text}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-500 text-xs">
                    {meta.offsetLabel
                      ? (rule.offset_minutes > 0 ? `+${rule.offset_minutes}m` : 'Immediate')
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700">×{rule.repeat_count}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleToggle(rule)}
                      title={rule.is_active ? 'Deactivate' : 'Activate'}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                        rule.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                    >
                      <Power size={14} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(rule)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(rule)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {rules.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-sm">
                  No baggage/weather rules found — the system is using built-in wording for all three. Click "New Rule" to customize one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {editingRule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Edit {BAGGAGE_TYPE_META[editingRule.announcement_type]?.label || editingRule.name}</h2>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Script</label>
                  <button
                    type="button"
                    onClick={() => setTemplatePickerFor('edit')}
                    className="flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
                  >
                    <FileText size={12} /> Use Template
                  </button>
                </div>
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  rows={5}
                  required
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                {BAGGAGE_TYPE_META[editingRule.announcement_type]?.placeholders.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Placeholders:{' '}
                    {BAGGAGE_TYPE_META[editingRule.announcement_type].placeholders.map(p => (
                      <code key={p} className="bg-gray-100 px-1 rounded mr-1">{`{${p}}`}</code>
                    ))}
                  </p>
                )}
              </div>
              <div className="flex gap-4">
                <div className="w-32">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Repeat Count</label>
                  <input
                    type="number"
                    value={repeatCount}
                    onChange={e => setRepeatCount(e.target.value)}
                    min={1}
                    max={5}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {BAGGAGE_TYPE_META[editingRule.announcement_type]?.offsetLabel && (
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {BAGGAGE_TYPE_META[editingRule.announcement_type].offsetLabel}
                    </label>
                    <input
                      type="number"
                      value={offsetMinutes}
                      onChange={e => setOffsetMinutes(e.target.value)}
                      min={0}
                      max={120}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">0 = announce immediately</p>
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="ghost" onClick={() => setEditingRule(null)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={saving} className="flex-1">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Create Modal ────────────────────────────────────────────────────── */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">New Baggage / Weather Rule</h2>
            </div>
            <form onSubmit={handleCreate} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Belt Change (v2)"
                  required
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select
                  value={createForm.announcement_type}
                  onChange={e => setCreateForm(f => ({ ...f, announcement_type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(BAGGAGE_TYPE_META).map(([value, meta]) => (
                    <option key={value} value={value}>{meta.label}</option>
                  ))}
                </select>
                {rules.some(r => r.announcement_type === createForm.announcement_type && r.is_active) && (
                  <p className="text-xs text-amber-600 mt-1">
                    An active "{BAGGAGE_TYPE_META[createForm.announcement_type]?.label}" rule already exists — creating
                    this one will deactivate it, since only one can be active per type.
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Script *</label>
                  <button
                    type="button"
                    onClick={() => setTemplatePickerFor('create')}
                    className="flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"
                  >
                    <FileText size={12} /> Use Template
                  </button>
                </div>
                <textarea
                  value={createForm.default_english_text}
                  onChange={e => setCreateForm(f => ({ ...f, default_english_text: e.target.value }))}
                  rows={5}
                  required
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                {BAGGAGE_TYPE_META[createForm.announcement_type]?.placeholders.length > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Placeholders:{' '}
                    {BAGGAGE_TYPE_META[createForm.announcement_type].placeholders.map(p => (
                      <code key={p} className="bg-gray-100 px-1 rounded mr-1">{`{${p}}`}</code>
                    ))}
                  </p>
                )}
              </div>

              <div className="flex gap-4">
                <div className="w-32">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Repeat Count</label>
                  <input
                    type="number"
                    value={createForm.repeat_count}
                    onChange={e => setCreateForm(f => ({ ...f, repeat_count: e.target.value }))}
                    min={1}
                    max={5}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {BAGGAGE_TYPE_META[createForm.announcement_type]?.offsetLabel && (
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {BAGGAGE_TYPE_META[createForm.announcement_type].offsetLabel}
                    </label>
                    <input
                      type="number"
                      value={createForm.offset_minutes}
                      onChange={e => setCreateForm(f => ({ ...f, offset_minutes: e.target.value }))}
                      min={0}
                      max={120}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">0 = announce immediately</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={saving} className="flex-1">
                  Create Rule
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ──────────────────────────────────────── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Delete "{deleteConfirm.name}"?</h3>
                <p className="text-sm text-gray-500">
                  The announcement will keep firing with its built-in default wording.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="flex-1">
                Cancel
              </Button>
              <Button variant="danger" onClick={() => handleDelete(deleteConfirm.id)} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {templatePickerFor && (
        <TemplatePickerModal
          onClose={() => setTemplatePickerFor(null)}
          onSelect={(pickedText) => {
            if (templatePickerFor === 'edit') setText(pickedText);
            else setCreateForm(f => ({ ...f, default_english_text: pickedText }));
          }}
        />
      )}
    </div>
  );
}
